import { Coordinate } from '@kinomap-dev/ng-ui-lib/core';
import { ContentType } from '../pages/content-creation/content-creation';
import { CreateContentService } from '../services/create-content.service';
import { MediaBlockService } from '../services/media-block-service';
import { ContentPreviewSource, ContentPreviewViewModel } from '../types/content-preview.type';
import {
  CONTENT_TYPE_MEDIA,
  ContentTypeMedia,
} from '../configs/content-creation/content-type-media.config';

export const getPreviewFromContent = (content: ContentPreviewSource): ContentPreviewViewModel => {
  return {
    title: content.name,
    thumbnailUrl: getThumbnailUrl(content),
    difficultyLevel: content.difficulty_level ?? undefined,
    likesCount: content.likes_count,
    duration: 'duration' in content ? content.duration : undefined,
    distance: 'distance' in content ? content.distance : undefined,
    positiveSlope: 'positive_slope' in content ? content.positive_slope : undefined,
    recordDate: 'recorded_date' in content ? content.recorded_date : undefined,
    uploadDate: 'added_date' in content ? content.added_date : getCreatedAt(content),
    visibility: content?.visibilty ? content?.visibilty : 'public',
    planningType: undefined,
    hasCoordinatesForGpsOnly: false,
  };
};

export const getPreviewFromCreationForm = (
  createContentService: CreateContentService,
  mediaBlockService: MediaBlockService,
  previewContentType: ContentType | undefined,
): ContentPreviewViewModel => {
  // Les medias charges pour un autre type restent en memoire : on ne lit que ceux du type courant.
  const media: ContentTypeMedia = previewContentType
    ? CONTENT_TYPE_MEDIA[previewContentType]
    : { video: false, gps: false };
  const coordinates: Coordinate[] = media.gps ? createContentService.coordinates() : [];

  return {
    title: createContentService.title(),
    thumbnailUrl: media.video ? createContentService.videoThumbnailUrl() : undefined,
    difficultyLevel: undefined,
    likesCount: undefined,
    duration: getMediaDuration(mediaBlockService, media),
    distance: getTotalDistance(coordinates),
    positiveSlope: getPositiveSlope(coordinates),
    recordDate: createContentService.recordDate(),
    uploadDate: createContentService.uploadDate(),
    visibility: createContentService.visibility(),
    planningType: createContentService.planningType(),
    hasCoordinatesForGpsOnly:
      !!coordinates.length &&
      previewContentType === 'gps-only' &&
      mediaBlockService.gpsTrackValidated(),
  };
};

const getMediaDuration = (
  mediaBlockService: MediaBlockService,
  media: ContentTypeMedia,
): number | undefined => {
  if (media.video && media.gps) return mediaBlockService.cumuledTime();
  if (media.video) return mediaBlockService.videoFileData()?.duration;
  if (media.gps) return mediaBlockService.gpsFileData()?.duration;
  return undefined;
};

export const getTotalDistance = (coordinates: Coordinate[]): number | undefined => {
  if (!coordinates.length) return undefined;

  return Math.round(coordinates.at(-1)!.distance);
};

export const getPositiveSlope = (coordinates: Coordinate[]): number | undefined => {
  if (!coordinates.length) return undefined;

  let positiveSlope: number = 0;
  for (let i: number = 1; i < coordinates.length; i++) {
    const previousElevation: number = coordinates[i - 1].elevation;
    const currentElevation: number = coordinates[i].elevation;
    if (isNaN(previousElevation) || isNaN(currentElevation)) continue;

    const elevationGain: number = currentElevation - previousElevation;
    if (elevationGain > 0) positiveSlope += elevationGain;
  }

  return Math.round(positiveSlope);
};

export const getCreatedAt = (content: ContentPreviewSource): string | undefined => {
  return 'created_at' in content ? content.created_at : undefined;
};

export const getThumbnailUrl = (content: ContentPreviewSource): string | undefined => {
  if (!content.thumbnails) return undefined;

  return typeof content.thumbnails === 'string' ? content.thumbnails : content.thumbnails.medium;
};

export const getContentTypeFromContent = (
  type: ContentPreviewSource['type'] | ContentType | undefined,
): ContentType | undefined => {
  switch (type) {
    case 'video':
      return 'scenic';
    case 'gps-track':
      return 'gps-only';
    default:
      return type as ContentType | undefined;
  }
};
